/**
 * Resumo Técnico — Thresholds por Estrutura (GUPM-206, fiel à techTable do V8, L4792).
 * Seções A/B/C colapsáveis (cores de identidade da estrutura), com badge de anomalia,
 * tabela Atenção/Alerta/Emergência + atalho "→ Fluxo", notas de instrumento sem faixa
 * e barra de filtros (tipo/estrutura/seção/status). Layout responsivo: telas largas
 * exibem A/B/C lado a lado (grid 3 col); telas estreitas empilham (como o protótipo).
 *
 * GUPM-230: saiu de `features/M1-dashboard/` para cá porque o Mapa (M2) passou a
 * usar o mesmo painel — e features não importam umas das outras (ADR-0008).
 *
 * Dois modos, declarados na prop `modo` — ver a tabela de diferenças em `PainelModo`.
 */
import { useMemo, useState } from 'react';
import {
  agruparInstrumentosPorBarragem, contagemDeCartaDaEstrutura, type SecaoBarragem,
} from '@/domain';
import type { Instrument, Reading } from '@/domain';
import {
  ehAnomaliaAcionavel, NIVEIS_EXIBIDOS, nivelDeInstrumento, SEM_LEITURA,
  SEM_SINAL, semSinalVisivel, type NivelExibido,
} from '@/domain/instrumentos/nivel-exibido';
import { unitForType } from '@/domain/instrumentos/instrument-type';
import { linhasDeTiltimetro, type FaixaCanal } from '@/domain/instrumentos/tilt';
import { NIVEL_EXIBIDO_UI } from '@/ui';
import TabelaTiltimetros from './TabelaTiltimetros';
import TabelaMarcos from './TabelaMarcos';
import { linhasDeMarco } from '@/domain/instrumentos/linhas-de-marco';
import type { DamOption } from '@/services/datasources';
import { foraDeServico } from '@/domain/instrumentos/fora-de-servico';
import { rotuloDaEstrutura } from '@/domain/estrutura/rotulo';

/** Rótulo da estrutura no padrão do V8 ("Barragem A/B/C") — ignora sufixos do cadastro. */
const damLabel = (code: string) => rotuloDaEstrutura(code) ?? code;

/**
 * Opções do filtro de status. Vem do domínio, não de uma lista própria.
 *
 * Era literal aqui, e ficou para trás quando o `SEM_SINAL` entrou no GUPM-303: a linha
 * aparecia na tabela, pintada e clicável, e o dropdown não oferecia o estado. O `tsc` não
 * avisa — `NivelExibido[]` aceita subconjunto, então lista incompleta compila.
 */
const STATUS_FILTRAVEIS = NIVEIS_EXIBIDOS;

/**
 * Limiar da Carta com 2 casas fixas. É PADDING, não arredondamento: nenhum limiar
 * da Carta passa de 2 decimais (medido: 83 valores com 2, 12 com 1, 1 inteiro), e
 * sem o padding "788.1" e "788.63" desalinham numa coluna à direita.
 */
const fmt = (n: number | null) => (n != null ? n.toFixed(2) : '—');
/**
 * Leitura com unidade (V8: "valor unidade"); '—' sem leitura; sem espaço órfão se
 * sem unidade. GUPM-221: 2 casas fixas — a API entrega até 4 (795.3478), que só
 * poluem a coluna sem informar nada ao RT.
 */
const fmtLeitura = (value: number | null, unit: string) =>
  value == null ? '—' : unit ? `${value.toFixed(2)} ${unit}` : value.toFixed(2);

/**
 * As duas telas que usam o painel. As diferenças entre elas, todas deliberadas:
 *
 * | | `dashboard` | `mapa` |
 * |---|---|---|
 * | instrumento sem Carta | nota de rodapé (fiel ao V8 — GUPM-206) | linha própria e clicável |
 * | barra de filtros | própria do painel | nenhuma — a tela já tem a sua |
 * | título do card | mostra | esconde (a lista já está em contexto) |
 * | gate de perfil | RT/admin (no `index` do M1) | nenhum |
 */
export type PainelModo = 'dashboard' | 'mapa';

interface Props {
  instruments: Instrument[];
  readings: Reading[];
  dams: DamOption[];
  /**
   * GUPM-270 — faixas vigentes por canal, fonte dos limiares da tabela de
   * tiltímetros. Ausente ⇒ a tabela aparece com os limites em "—" (o instrumento
   * não some da tela por falta de régua — esse é justamente o estado que o card
   * veio tornar visível).
   */
  faixasCanal?: FaixaCanal[];
  onFluxo: (instrumentId: string) => void;
  /**
   * Modo mapa: clicar no código destaca o pino. Presente ⇒ instrumentos sem Carta
   * também ganham linha própria (ver doc do módulo).
   */
  onSelect?: (id: string) => void;
  selectedId?: string | null;
  /**
   * Tela que hospeda o painel. Um enum em vez de um booleano por diferença: já são
   * três (nota de sem-Carta × linha, barra de filtros, título), e cada pedido novo
   * viraria outra flag solta que o chamador tem de acertar por conta.
   */
  modo?: PainelModo;
  className?: string;
}

export default function ThresholdsPorEstrutura({
  instruments,
  readings,
  dams,
  faixasCanal = [],
  onFluxo,
  onSelect,
  selectedId = null,
  modo = 'dashboard',
  className = '',
}: Props) {
  const noMapa = modo === 'mapa';
  const secoes = useMemo(
    () => agruparInstrumentosPorBarragem(instruments, readings, dams),
    [instruments, readings, dams],
  );

  // Filtros (fiel ao V8: tipo/estrutura/seção/status). '' = todos.
  const [fTipo, setFTipo] = useState('');
  const [fDam, setFDam] = useState('');
  const [fSec, setFSec] = useState('');
  const [fStatus, setFStatus] = useState('');
  // Sobrescreve o defaultOpen quando o usuário clica (damId ausente = usa defaultOpen).
  const [openMap, setOpenMap] = useState<Record<string, boolean>>({});

  const tipos = useMemo(() => [...new Set(instruments.map((i) => i.type))].sort(), [instruments]);
  const seçõesDisponiveis = useMemo(
    () => [...new Set(instruments.map((i) => i.section).filter((s): s is string => !!s))].sort(),
    [instruments],
  );

  const match = (type: string, section: string | null, level: NivelExibido) =>
    (!fTipo || type === fTipo) && (!fSec || section === fSec) && (!fStatus || level === fStatus);

  /**
   * Filtrar é pedir para VER o recorte, então qualquer filtro ativo abre as seções
   * que sobraram. Antes só o de estrutura abria: nas barragens A e C, que vêm
   * colapsadas (`defaultOpen` = B ou anomalia), o contador dizia "1 de 84
   * instrumentos" e a tela não mostrava linha nenhuma — o filtro parecia morto.
   * Medido no Mapa em 05/08/2026 com o status "Sem leitura".
   */
  const algumFiltro = !!(fTipo || fDam || fSec || fStatus);

  /**
   * Leitura atual de um instrumento sem faixa de controle. No modo mapa ele vira
   * linha, e a linha precisa de status E de valor: sem FAIXA não é sem LEITURA
   * (GUPM-227 — 48 dos 84 instrumentos estão nessa situação). O que falta nessas
   * linhas são os três limiares, e é só isso que fica em "—".
   */
  const leituraSemCarta = (id: string): Reading | null =>
    readings.find((r) => r.instrumentId === id) ?? null;
  /**
   * GUPM-247 — estes instrumentos são, por construção, os SEM faixa de controle
   * (`semLimiar`), então o nível deles nunca pode ser um dos 4 medidos: passa
   * `temFaixa = false` e o resultado é `SEM_FAIXA` (ou `SEM_LEITURA`, que tem
   * precedência quando nem leitura existe).
   */
  const nivelSemCarta = (id: string): NivelExibido => {
    // GUPM-303 — o frescor vem do cadastro, nao da leitura: um instrumento sem carta
    // que emudeceu pede telemetria, nao cobranca de carta. Instrumento ausente da
    // lista cai em `false` (nao acusa o que nao se conhece).
    const inst = instruments.find((i) => i.id === id);
    return nivelDeInstrumento(
      leituraSemCarta(id),
      false,
      inst ? semSinalVisivel(inst) : false,
      // GUPM-307 — declarado fora de servico vence "sem carta": cobrar carta de um
      // instrumento retirado de campo e pedir trabalho que nao vai acontecer.
      inst ? foraDeServico(inst) : false,
    );
  };
  const valorSemCarta = (id: string): number | null => leituraSemCarta(id)?.value ?? null;

  /**
   * GUPM-270 — nível do tiltímetro para o FILTRO de status. Precisa da mesma regra
   * da tabela (pior eixo com faixa), senão filtrar por "Alerta" esconderia um
   * tiltímetro que a tabela mostra em Alerta.
   */
  const nivelDeTilt = (id: string): NivelExibido => {
    const inst = instruments.find((i) => i.id === id);
    if (!inst) return SEM_LEITURA;
    const [linha] = linhasDeTiltimetro([inst], readings, faixasCanal);
    return linha?.nivel ?? SEM_LEITURA;
  };

  // Aplica os filtros por seção; filtro por estrutura mostra só aquela barragem.
  //
  // GUPM-270 — o grupo de tiltímetros entra na conta e nos filtros como os outros
  // dois. Ao virar um bucket próprio ele ficou de fora de ambos: o contador dizia
  // "x de 74" onde há 84 instrumentos, e filtrar por Tipo "INA" continuava exibindo
  // a tabela de tiltímetros inteira — o filtro parecia não ter funcionado.
  const total = secoes.reduce(
    (n, s) => n + s.comLimiar.length + s.semLimiar.length + s.tiltimetros.length + s.marcos.length,
    0,
  );
  let shown = 0;
  const visiveis = secoes
    .filter((s) => !fDam || s.dam.code === fDam)
    .map((s) => {
      const comLimiar = s.comLimiar.filter((x) => match(x.instrument.type, x.instrument.section, x.level));
      const semLimiar = s.semLimiar.filter((i) => match(i.type, i.section, nivelSemCarta(i.id)));
      const tiltimetros = s.tiltimetros.filter((i) => match(i.type, i.section, nivelDeTilt(i.id)));
      // GUPM-329 — marco entra na contagem de visíveis como qualquer outro: a seção
      // só é escondida quando NADA nela passa no filtro.
      const marcos = s.marcos.filter((i) => match(i.type, i.section, nivelDeTilt(i.id)));
      shown += comLimiar.length + semLimiar.length + tiltimetros.length + marcos.length;
      // Com filtro ativo a seção abre (V8 já fazia isso no filtro de estrutura);
      // sem filtro, respeita o clique do usuário e depois o defaultOpen.
      const open = algumFiltro ? true : (openMap[s.dam.id] ?? s.defaultOpen);
      return { secao: s, comLimiar, semLimiar, tiltimetros, marcos, open };
    })
    .filter((v) => v.comLimiar.length + v.semLimiar.length + v.tiltimetros.length + v.marcos.length > 0);

  const limpar = () => {
    setFTipo('');
    setFDam('');
    setFSec('');
    setFStatus('');
  };

  const selCls =
    'text-[11px] bg-jf-bg text-white border border-jf-border rounded-md py-1 pl-2 pr-6 cursor-pointer';

  return (
    <div className={`rounded-xl border border-jf-border bg-jf-surface p-4 ${className}`}>
      {/* No Mapa o título sai (a lista já está em contexto), mas a dica de
          expandir/colapsar fica — ela explica o clique no cabeçalho da seção. */}
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        {!noMapa && (
          <h2 className="flex items-center gap-2 text-[15px] font-bold text-white">
            🔬 Resumo Técnico — Thresholds por Estrutura
          </h2>
        )}
        <span className="ml-auto text-[10px] font-normal text-jf-dim">
          Clique no cabeçalho para expandir/colapsar
        </span>
      </div>

      {/* Barra de filtros — some no Mapa, que tem a sua própria (ver `PainelModo`). */}
      {!noMapa && (
      <div className="mb-3 flex flex-wrap items-center gap-1.5 border-b border-jf-border pb-2.5">
        <span className="mr-0.5 text-[10px] font-bold uppercase tracking-wide text-jf-dim">Filtrar:</span>
        <select aria-label="Filtrar por tipo" className={selCls} value={fTipo} onChange={(e) => setFTipo(e.target.value)}>
          <option value="">Tipo: todos</option>
          {tipos.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        <select aria-label="Filtrar por estrutura" className={selCls} value={fDam} onChange={(e) => setFDam(e.target.value)}>
          <option value="">Estrutura: todas</option>
          {secoes.map((s) => (
            <option key={s.dam.id} value={s.dam.code}>{damLabel(s.dam.code)}</option>
          ))}
        </select>
        <select aria-label="Filtrar por seção" className={selCls} value={fSec} onChange={(e) => setFSec(e.target.value)}>
          <option value="">Seção: todas</option>
          {seçõesDisponiveis.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <select aria-label="Filtrar por status" className={selCls} value={fStatus} onChange={(e) => setFStatus(e.target.value)}>
          <option value="">Status: todos</option>
          {STATUS_FILTRAVEIS.map((l) => (
            <option key={l} value={l}>{NIVEL_EXIBIDO_UI[l].label}</option>
          ))}
        </select>
        <button
          type="button"
          onClick={limpar}
          className="rounded-md border border-jf-border bg-transparent px-2 py-1 text-[10px] text-jf-muted hover:text-white"
        >
          Limpar
        </button>
        {algumFiltro && (
          <span className="ml-1 text-[10px] text-level-atencao">{shown} de {total} instrumentos</span>
        )}
      </div>
      )}

      {/* Responsivo: empilha (vertical, como o V8) e vai a 3 colunas lado a lado só em
          telas bem largas (2xl ≈ 1536px) — abaixo disso, 3 tabelas densas cortariam
          colunas. Cada tabela rola na horizontal dentro do seu card (overflow-x-auto). */}
      <div className="grid grid-cols-1 gap-3 2xl:grid-cols-3">
        {visiveis.map(({ secao, comLimiar, semLimiar, tiltimetros, marcos, open }) => (
          <SecaoEstrutura
            key={secao.dam.id}
            secao={secao}
            comLimiar={comLimiar}
            semLimiar={semLimiar}
            tiltimetros={tiltimetros}
            marcos={marcos}
            faixasCanal={faixasCanal}
            readings={readings}
            open={open}
            onToggle={() => setOpenMap((m) => ({ ...m, [secao.dam.id]: !open }))}
            onFluxo={onFluxo}
            onSelect={onSelect}
            selectedId={selectedId}
            nivelSemCarta={nivelSemCarta}
            valorSemCarta={valorSemCarta}
            semCartaComoLinha={noMapa}
          />
        ))}
      </div>

      {/* Nota de Fontes (versão segura, sem códigos de documento — GUPM-199 notas V2). */}
      <p className="mt-3 px-1 text-[10px] leading-relaxed text-jf-dim">
        {/* GUPM-227: fonte única. A Carta de Controle (Walm/2025) saiu do banco —
            creditá-la aqui atribuiria a faixa exibida a uma fonte que não a produziu. */}
        Fonte: inventário GPX v2 (faixas de controle e cadastro) · leituras via GPX v2.
      </p>
    </div>
  );
}

interface SecaoProps {
  secao: SecaoBarragem<Instrument>;
  comLimiar: SecaoBarragem<Instrument>['comLimiar'];
  semLimiar: Instrument[];
  /** Já recortados pelos filtros do painel (tipo/seção/status). */
  tiltimetros: Instrument[];
  marcos: Instrument[];
  faixasCanal: FaixaCanal[];
  readings: Reading[];
  open: boolean;
  onToggle: () => void;
  onFluxo: (instrumentId: string) => void;
  onSelect?: (id: string) => void;
  selectedId: string | null;
  nivelSemCarta: (id: string) => NivelExibido;
  valorSemCarta: (id: string) => number | null;
  /** Modo mapa: a lista é o índice dos pinos, então sem-Carta também vira linha. */
  semCartaComoLinha: boolean;
}

function SecaoEstrutura({
  secao, comLimiar, semLimiar, tiltimetros, marcos, faixasCanal, readings, open, onToggle, onFluxo,
  onSelect, selectedId, nivelSemCarta, valorSemCarta, semCartaComoLinha,
}: SecaoProps) {
  // GUPM-275 — cabeçalho NEUTRO (decisão do dono em 13/08/2026). A cor de identidade
  // da barragem (roxo/azul/vermelho, do V8 — ADR-0018) disputava leitura com a cor de
  // SEVERIDADE na mesma tela: "Barragem C" em vermelho ao lado de Emergência em
  // vermelho. Num painel de segurança, cor tem de significar uma coisa só.
  // `damColor` segue em uso no chip do M6, onde não há semáforo competindo.
  // GUPM-275 — a conta é do DOMÍNIO: só ativo, e tiltímetro conta (ele tem tabela
  // própria, mas existe). Antes era `secao.semLimiar.length` cru, e o cabeçalho da
  // Barragem B dizia "21 s/ carta" (3 ativos + 18 retirados) contra o "4" do card
  // da mesma tela.
  const { comCarta: totalComLimiar, semCarta: totalSemLimiar } =
    contagemDeCartaDaEstrutura(secao);

  // GUPM-270 — tiltímetro tem tabela própria, logo abaixo da principal. Retirado
  // vira nota de rodapé lá dentro, como na tabela principal. A lista já vem
  // recortada pelos filtros do painel.
  const tiltAtivos = tiltimetros.filter((i) => i.isActive);
  const tiltRetirados = tiltimetros.filter((i) => !i.isActive);
  const marcosAtivos = marcos.filter((i) => i.isActive);
  const marcosRetirados = marcos.filter((i) => !i.isActive);
  const linhasMarcos = linhasDeMarco(marcosAtivos, readings);
  // GUPM-270 — mesmo corte no grupo sem-carta: ativo é linha (ou nota de "sem
  // limite"), retirado é só a nota de retirados.
  const semAtivos = semLimiar.filter((i) => i.isActive);
  const semRetirados = semLimiar.filter((i) => !i.isActive);
  const linhasTilt = linhasDeTiltimetro(tiltAtivos, readings, faixasCanal);
  return (
    <div className="min-w-0">
      <button
        type="button"
        aria-expanded={open}
        onClick={onToggle}
        className="flex w-full items-center justify-between rounded-[10px] border border-jf-border bg-jf-surface2 px-3 py-2.5 text-left transition-colors hover:border-jf-muted hover:bg-jf-bg"
      >
        <span className="flex items-center gap-2">
          <span className="text-[13px] font-extrabold text-white">{damLabel(secao.dam.code)}</span>
          {secao.hasAnomaly && (
            <span
              className="rounded-full border border-level-emergencia/40 bg-level-emergencia/20 px-2 py-0.5 text-[10px] font-bold"
              style={{ color: '#fca5a5' }}
            >
              ⚠️ anomalia
            </span>
          )}
        </span>
        <span className="flex items-center gap-2">
          <span className="text-[10px] text-jf-dim">
            {totalComLimiar} c/ limites{totalSemLimiar ? ` · ${totalSemLimiar} s/ carta` : ''}
          </span>
          <span className="text-jf-dim" aria-hidden>{open ? '▲' : '▼'}</span>
        </span>
      </button>

      {open && (
        <div className="mt-1.5 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-jf-border text-left text-jf-dim">
                <th className="pb-2 pr-3 font-semibold">Instrumento</th>
                <th className="pb-2 pr-3 font-semibold">Tipo</th>
                <th className="pb-2 pr-3 font-semibold">Seção</th>
                {/* Colunas numéricas alinhadas à DIREITA, cabeçalho junto (GUPM-221).
                    A tarefa do RT nesta tabela é comparar a leitura com os três
                    limiares na mesma linha e varrer a mesma grandeza coluna abaixo —
                    à direita, com a fonte mono, as casas decimais empilham e a
                    comparação é visual. Centralizar deixaria a borda serrilhada dos
                    dois lados e quebraria justamente essa leitura vertical. */}
                <th className="pb-2 pr-3 text-right font-semibold">Última leitura</th>
                <th className="pb-2 pr-3 text-right font-semibold text-level-atencao">Atenção</th>
                <th className="pb-2 pr-3 text-right font-semibold text-level-alerta">Alerta</th>
                <th className="pb-2 pr-3 text-right font-semibold text-level-emergencia">Emergência</th>
                {/* O atalho para o fluxo TARP vive DENTRO desta célula (GUPM-221):
                    uma coluna inteira para um botão que aparece em poucas linhas
                    espremia as demais sem necessidade. */}
                <th className="pb-2 pr-3 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {comLimiar.map(({ instrument: i, level, value, unit }) => (
                <LinhaInstrumento
                  key={i.id}
                  instrument={i}
                  level={level}
                  value={value}
                  unit={unit}
                  onFluxo={onFluxo}
                  onSelect={onSelect}
                  selected={selectedId === i.id}
                />
              ))}
              {/* GUPM-270 — só instrumento ATIVO vira linha, nos dois modos. No modo
                  mapa TODO sem-carta ganhava linha própria e clicável, retirado
                  incluído: os 26 retirados (0 com carta, 0 com coordenada) viravam 26
                  linhas que apontavam para um pino que não existe. */}
              {semCartaComoLinha && semAtivos.map((i) => (
                <LinhaInstrumento
                  key={i.id}
                  instrument={i}
                  level={nivelSemCarta(i.id)}
                  value={valorSemCarta(i.id)}
                  unit={unitForType(i.type)}
                  onFluxo={onFluxo}
                  onSelect={onSelect}
                  selected={selectedId === i.id}
                />
              ))}
              {/* GUPM-227: duas notas, não uma. A nota única dizia que TODOS
                  "aguardam Carta de Controle de Deslocamento (JF Brasil)" — o que
                  ficou falso duas vezes: a Carta saiu do banco (a faixa vem do
                  inventário v2) e instrumento retirado não aguarda nada. Na Barragem
                  B são 12 sem faixa e 21 retirados, que antes apareciam somados como
                  33 "aguardando".

                  GUPM-270 — a nota de "Sem limite" só no dashboard (no mapa eles são
                  linha), mas a de RETIRADOS nos DOIS: é o único lugar onde o retirado
                  aparece agora, e sem ela ele desapareceria da tela em vez de ficar
                  registrado como fora do monitoramento. */}
              {!semCartaComoLinha && semAtivos.length > 0 && (
                <tr>
                  <td colSpan={8} className="py-2 text-[10px] italic text-jf-dim">
                    {`Sem limite definido: ${semAtivos
                      .map((i) => i.code)
                      .join(', ')} — o inventário GPX v2 não informa os 3 pontos (atenção/alerta/emergência) para estes instrumentos.`}
                  </td>
                </tr>
              )}
              {semRetirados.length > 0 && (
                <tr>
                  <td colSpan={8} className="py-2 text-[10px] italic text-jf-dim">
                    {`Retirados (histórico preservado, fora do monitoramento): ${semRetirados
                      .map((i) => i.code)
                      .join(', ')}.`}
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <TabelaTiltimetros
            linhas={linhasTilt}
            retirados={tiltRetirados}
            onFluxo={onFluxo}
            onSelect={onSelect}
            selectedId={selectedId}
          />

          <TabelaMarcos
            linhas={linhasMarcos}
            retirados={marcosRetirados}
            onSelect={onSelect}
            selectedId={selectedId}
          />
        </div>
      )}
    </div>
  );
}

interface LinhaProps {
  instrument: Instrument;
  level: NivelExibido;
  value: number | null;
  unit: string;
  onFluxo: (instrumentId: string) => void;
  onSelect?: (id: string) => void;
  selected: boolean;
}

function LinhaInstrumento({ instrument: i, level, value: valorBruto, unit, onFluxo, onSelect, selected }: LinhaProps) {
  const ui = NIVEL_EXIBIDO_UI[level];
  // Anomalia = há ação a tomar a partir desta linha. "Sem leitura" e "sem faixa" não
  // têm fluxo TARP a abrir.
  // GUPM-247 — a guarda antiga (`!== 'NORMAL' && !== SEM_LEITURA`) estava correta
  // enquanto SEM_LEITURA era o único estado sem lastro; com SEM_FAIXA ela transformava
  // o chip de status em BOTÃO "Abrir fluxo TARP" — convidando a abrir emergência para
  // instrumento que só não tem régua.
  // GUPM-303 — `ehAnomaliaAcionavel` e não `ehNivelMedido`: aquela guarda continua
  // valendo para os dois estados acima, mas o SEM_SINAL tem protocolo próprio no
  // catálogo (`TARP-PS-TILT`) e precisa oferecer o atalho.
  const anomalia = ehAnomaliaAcionavel(level);
  // GUPM-303 — a última leitura de um instrumento mudo é um número CONGELADO. Exibi-lo
  // na cor do nível afirmaria "medido e dentro da faixa" sobre um dado que parou de
  // chegar; a série completa fica no M6.
  const value = level === SEM_SINAL ? null : valorBruto;
  return (
    <tr
      aria-selected={onSelect ? selected : undefined}
      className={selected ? 'bg-jf-yellow/10' : undefined}
      style={anomalia && !selected ? { background: ui.bg } : undefined}
    >
      <td className="py-1.5 pr-3">
        {onSelect ? (
          <button
            type="button"
            onClick={() => onSelect(i.id)}
            aria-label={`Destacar ${i.code} no mapa`}
            className="cursor-pointer font-mono font-bold underline underline-offset-2"
            style={{ color: ui.color }}
          >
            {i.code}
          </button>
        ) : (
          <span className="font-mono font-bold" style={{ color: ui.color }}>{i.code}</span>
        )}
      </td>
      <td className="py-1.5 pr-3 text-jf-muted">{i.type}</td>
      <td className="py-1.5 pr-3 text-jf-muted">{i.section ?? '—'}</td>
      {/* Última leitura é o dado que o RT procura primeiro na linha —
          cor do nível e peso forte (era `text-jf-dim`, ilegível). Sem
          leitura fica apagado: '—' não tem nível a comunicar. */}
      <td
        className={`py-1.5 pr-3 text-right font-mono font-bold ${value == null ? 'text-jf-dim' : ''}`}
        style={value == null ? undefined : { color: ui.color }}
      >
        {fmtLeitura(value, unit)}
      </td>
      <td className="py-1.5 pr-3 text-right font-mono text-level-atencao">{fmt(i.levelAtencaoMax)}</td>
      <td className="py-1.5 pr-3 text-right font-mono text-level-alerta">{fmt(i.levelAlertaMax)}</td>
      <td className="py-1.5 pr-3 text-right font-mono text-level-emergencia">{fmt(i.levelEmergenciaMax)}</td>
      <td className="py-1.5 pr-3">
        {anomalia ? (
          // O próprio chip de status é o atalho. Chip + botão separado
          // custava 43px de largura numa coluna que 1 linha em 33 usa,
          // e o vão entre os dois relia como uma coluna a mais.
          // A borda em `ui.color` (não `ui.border`) é o que sinaliza
          // que este chip é clicável e o da linha ao lado não é.
          <button
            type="button"
            onClick={() => onFluxo(i.id)}
            aria-label={`Abrir fluxo TARP para ${i.code}`}
            title="Abrir fluxo TARP"
            className="rounded-full border px-2 py-0.5 text-[10px] font-black transition hover:brightness-125"
            style={{ color: ui.color, borderColor: ui.color, background: ui.bg }}
          >
            {ui.label} →
          </button>
        ) : (
          <span
            // GUPM-247 — estado sem lastro de faixa vem VAZADO (borda tracejada):
            // distinção por forma, não só por matiz (WCAG 1.4.1). Sem isto,
            // "Sem faixa" e "Sem leitura" diferiam apenas por um tom de cinza.
            className={`rounded-full border px-2 py-0.5 text-[10px] font-black ${
              ui.outline ? 'border-dashed' : ''
            }`}
            style={{ color: ui.color, borderColor: ui.border, background: ui.bg }}
          >
            {ui.label}
          </span>
        )}
      </td>
    </tr>
  );
}
